import { StrictMode } from "react";
import * as Sentry from "@sentry/react";
import { Provider } from "react-redux";
import { createRoot } from "react-dom/client";

import store from "@store/store";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import MuiThemeProvider from "@providers/MuiThemeProvider";
import UnleashProvider from "@providers/UnleashProvider";
import SnackbarProvider from "@providers/SnackbarProvider";
import LocalizationProvider from "@providers/LocalizationProvider";
import AmplitudeProvider from "@providers/AmplitudeProvider";
import MagicFeedbackProvider from "@providers/MagicFeedbackProvider";

import ChunkErrorBoundary from "@components/ChunkErrorBoundary";

import PageRoutes from "@routes";

import { captureMarketingTrackingFromUrl, markRouterInitialized } from "@utils";

import "./i18n";

captureMarketingTrackingFromUrl(window.location.search);

const { VITE_SENTRY_URL, VITE_IS_LOCAL_API_ENABLED, MODE } = import.meta.env;

if (VITE_IS_LOCAL_API_ENABLED === "true" && import.meta.env.DEV && MODE !== "cypress") {
  const { default: makeServer } = await import("./mirage");
  makeServer();
}

if (VITE_SENTRY_URL != null) {
  Sentry.init({
    dsn: VITE_SENTRY_URL,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    ignoreErrors: [
      "Cannot read properties of undefined (reading 'consent')",
      "window.CookieConsent is undefined",
      "undefined is not an object (evaluating 'window.CookieConsent.consent')",
    ],
    replaysOnErrorSampleRate: 1,
  });
}
const router = createBrowserRouter(PageRoutes);
if (router.state.initialized) {
  markRouterInitialized();
}
const unsubscribeRouterInitialized = router.subscribe((state) => {
  if (state.initialized) {
    markRouterInitialized();
    unsubscribeRouterInitialized();
  }
});

const AppToRender = () => (
  <StrictMode>
    <ChunkErrorBoundary>
      <Provider store={store}>
        <MuiThemeProvider>
          <UnleashProvider>
            <SnackbarProvider>
              <LocalizationProvider>
                <AmplitudeProvider>
                  <MagicFeedbackProvider>
                    <RouterProvider router={router} />
                  </MagicFeedbackProvider>
                </AmplitudeProvider>
              </LocalizationProvider>
            </SnackbarProvider>
          </UnleashProvider>
        </MuiThemeProvider>
      </Provider>
    </ChunkErrorBoundary>
  </StrictMode>
);

const domNode = document.querySelector("#application-spa");
if (domNode != null) {
  const root = createRoot(domNode);
  root.render(<AppToRender />);
}
